import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import { parseISODate } from '../utils/date';
import { EquipeLite, PlacarLite, PartidaLite } from './faseGrupoPartidasService';
import {
  ModeloDisputa,
  TipoPontuacao,
  TipoGrupoChave,
  StatusCategoriaTorneio,
  GeneroCategoria,
} from '../types/categoriaTorneio';

export {
  ModeloDisputa,
  TipoPontuacao,
  TipoGrupoChave,
  StatusCategoriaTorneio,
  GeneroCategoria,
};

export const getStatusCategoriaTorneioDisplay = (status?: string | null) => {
  switch (status) {
    case StatusCategoriaTorneio.AGENDADA:
      return { label: 'Agendada', color: 'bg-warning' };
    case StatusCategoriaTorneio.EM_ANDAMENTO:
      return { label: 'Em Andamento', color: 'bg-success' };
    case StatusCategoriaTorneio.ENCERRADA:
      return { label: 'Encerrada', color: 'bg-secondary' };
    case StatusCategoriaTorneio.INSCRICAO_ABERTA:
      return { label: 'Inscrição Aberta', color: 'bg-primary' };
    case StatusCategoriaTorneio.INSCRICAO_ENCERRADA:
      return { label: 'Inscrição Encerrada', color: 'bg-info' };
    default:
      return { label: 'Indefinido', color: 'bg-secondary' };
  }
};

export interface FaseCategoriaTorneio {
  id: string;
  nome: string;
  numeroAtletas: number;
  dataInicio: Date;
  dataFim: Date;
  status: string;
  ordem: number;
  tipoGrupoChave?: string | null;
  partidas?: PartidaLite[];
}

export interface CategoriaAtleta {
  id: string;
  nome: string;
  descricao?: string | null;
}

export interface CategoriaTorneio {
  id: string;
  nome: string;
  descricao?: string | null;
  genero?: string | null;
  numeroAtletas: number;
  precoInscricao: number;
  aplicarDescontoTorneio: boolean;
  configFilaEspera?: boolean | null;
  inscricoesAtivasCount?: number | null;
  quantidadeFases: number;
  regulamento?: string | null;
  status?: string | null;
  vagas?: number | null;
  dataInicio?: string | null;
  dataFim?: string | null;
  dataInicioInscricao?: string | null;
  dataFimInscricao?: string | null;
  dataLimPag?: string | null;
  modeloDisputa?: string | null;
  formatoPartidas?: string | null;
  idadeInicial?: number | null;
  idadeFinal?: number | null;
  idadeCombinada?: number | null;
  quantidadeGrupos?: number | null;
  classificadosPorGrupo?: number | null;
  tamanhoGrupo?: number | null;
  tipoPontuacao?: string | null;
  numeroSets?: number | null;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
  // Vínculo com a CategoriaLiga escolhida como template (AC-2/AC-4 do PRD
  // categoria-liga) — opcional, preenchido só quando a categoria foi criada/editada
  // a partir de um template da liga.
  categoriaLiga?: { id: string; nome?: string | null } | null;
  torneio: {
    id: string;
    nome: string;
    avatarUrl?: string | null;
    dataInicio?: string | null;
    dataFim?: string | null;
    dataInscricaoInicio?: string | null;
    dataInscricaoFim?: string | null;
    status?: string | null;
    validarElegibilidade?: boolean | null;
    liga?: { id: string; nome?: string | null } | null;
    // `Torneio.local` é OPCIONAL no backend — `torneio.graphqls` declara
    // `local: Local` (sem `!`) e `Torneio.java` mapeia `local_id` sem
    // `nullable = false`. Torneio avulso pode não ter local: todo consumidor
    // precisa de null-guard.
    local?: {
      id: string; 
      nome: string;
      endereco: {
        id: string;
        logradouro: string;
        numero: string;
        complemento?: string | null;
        bairro: string;
        cidade: {
          id: string;
          nome: string;
          uf: string;
        };
        cep: string;
      };
    } | null;
  };
  fasesCategoria: FaseCategoriaTorneio[];
  partidas?: { id: string }[];
}


export interface CategoriaTorneioPorAtletaData {
  categoriaTorneioPorAtleta: CategoriaTorneio[];
}


export interface CategoriaTorneioPorAtletaVars {
  idAtleta: string;
  statusInscricao: string;
}

export const categoriaTorneioPorAtleta = async (
  idAtleta: string,
  statusInscricao: string,
) => {
  const { data } = await apolloClient.query<
    CategoriaTorneioPorAtletaData,
    CategoriaTorneioPorAtletaVars
  >({
    query: CATEGORIAS_TORNEIO_POR_ATLETA,
    variables: { idAtleta, statusInscricao },
    fetchPolicy: 'no-cache',
  });
  return data.categoriaTorneioPorAtleta;
};

export const deletarCategoriaTorneio = async (id: string) => {
  const { data } = await apolloClient.mutate<{ deletarCategoriaTorneio: boolean }>({
    mutation: DELETAR_CATEGORIA_TORNEIO,
    variables: { id },
  });
  return data?.deletarCategoriaTorneio ?? false;
};

export const deletarTodasEntidadesCategoria = async (categoriaTorneioId: string): Promise<boolean> => {
  try {
    const { data } = await apolloClient.mutate<{ deletarTodasEntidadesCategoria: boolean }>({
      mutation: DELETAR_TODAS_ENTIDADES_CATEGORIA,
      variables: { categoriaTorneioId },
    });
    return data?.deletarTodasEntidadesCategoria ?? false;
  } catch (e) {
    throw new Error('Erro ao deletar todas as entidades da categoria');
  }
};


export const CATEGORIA_TORNEIO_POR_ID = gql`
  query CategoriaTorneioPorId($id: ID!) {
    categoriaTorneioPorId(id: $id) {
      id
      nome
      aplicarDescontoTorneio
      configFilaEspera
      inscricoesAtivasCount
      descricao
      genero
      numeroAtletas
      precoInscricao
      quantidadeFases
      regulamento
      status
      vagas
      dataInicio
      dataFim
      dataLimPag
      modeloDisputa
      idadeInicial
      idadeFinal
      idadeCombinada
      quantidadeGrupos
      classificadosPorGrupo
      tamanhoGrupo
      tipoPontuacao
      numeroSets
      gamesPorSets
      tiebreaks
      categoriaLiga {
        id
        nome
      }
      torneio {
        id
        nome
        avatarUrl
        dataInscricaoInicio
        dataInscricaoFim
        validarElegibilidade
        liga {
          id
          nome
        }
        local {
          id
          nome
          endereco {
            id
            logradouro
            numero
            complemento
            bairro
            cidade {
              id
              nome
              uf
            }
            cep
          }
        }
      }
      fasesCategoria {
        id
        nome
        numeroAtletas
        dataInicio
        dataFim
        status
        ordem
        tipoGrupoChave
        partidas {
          id
          numeroPartida
          data
          equipeVencedora
          equipeAWO
          equipeBWO
          equipeADesistencia
          equipeBDesistencia
          numeroSets
          gamesPorSets
          tiebreaks
          local { id nome }
          status
          equipeA {
            id
            nome
            atletas {
              id
              usuario { id nome imagemUrl }
            }
          }
          equipeB {
            id
            nome
            atletas {
              id
              usuario { id nome imagemUrl }
            }
          }
          placar {
            id
            resultadoFinalA
            resultadoFinalB
            sets {
              gamesA
              gamesB
              numeroSet
              tiebreakA
              tiebreakB
            }
          }
        }
      }
      partidas {
        id
      }
    }
  }
`;

export const CATEGORIAS_TORNEIO_POR_ATLETA = gql`
  query categoriaTorneioPorAtleta($idAtleta: ID!, $statusInscricao: StatusInscricao!) {
    categoriaTorneioPorAtleta(idAtleta: $idAtleta, statusInscricao: $statusInscricao) {
      descricao
      genero
      id
      numeroAtletas
      nome
      precoInscricao
      aplicarDescontoTorneio
      configFilaEspera
      inscricoesAtivasCount
      quantidadeFases
      regulamento
      status
      vagas
      dataInicio
      dataFim
      dataLimPag
      modeloDisputa
      idadeInicial
      idadeFinal
      idadeCombinada
      torneio {
        nome
        avatarUrl
        dataInicio
        dataFim
        status
        local {
          id
          nome
          descricao
          endereco {
            cidade {
              id
              nome
              uf
            }
          }
        }
        id
      }
      partidas {
        data
        status
        numeroSets
        gamesPorSets
        tiebreaks
        faseCategoriaTorneio {
          id
          nome
          ordem
        }
        placar {
          id 
          resultadoFinalA
          resultadoFinalB
        }
        equipeA {
          id
          atletas {
            id
            usuario {
              id
              nome
              imagemUrl
            }
          }
          partidasCategoriaTorneio
        }
        equipeB {
          id
          atletas {
            id
            usuario {
              id
              nome
              imagemUrl
            }
          }
          partidasCategoriaTorneio
        }
      }
    }
  }
`;


export const CRIAR_CATEGORIA_TORNEIO = gql`
  mutation CriarCategoriaTorneio($input: CategoriaTorneioInput!) {
    criarCategoriaTorneio(input: $input) {
      id
    }
  }
`;

export const ATUALIZAR_CATEGORIA_TORNEIO = gql`
  mutation AtualizarCategoriaTorneio($id: ID!, $input: CategoriaTorneioInput!) {
    atualizarCategoriaTorneio(id: $id, input: $input) {
      id
      nome
      aplicarDescontoTorneio
      configFilaEspera
      inscricoesAtivasCount
      descricao
      genero
      numeroAtletas
      precoInscricao
      regulamento
      status
      vagas
      dataInicio
      dataFim
      dataLimPag
      modeloDisputa
      idadeInicial
      idadeFinal
      idadeCombinada
      quantidadeGrupos
      classificadosPorGrupo
      tamanhoGrupo
      tipoPontuacao
      numeroSets
      gamesPorSets
      tiebreaks
      categoriaLiga {
        id
        nome
      }
    }
  }
`;

export const ALTERAR_STATUS_CATEGORIA_TORNEIO = gql`
  mutation AlterarStatusCategoriaTorneio($id: ID!, $status: StatusCategoriaTorneio!) {
    alterarStatusCategoriaTorneio(id: $id, status: $status) {
      id
      status
    }
  }
`;


export const REABRIR_CATEGORIA_TORNEIO = gql`
  mutation ReabrirCategoriaTorneio($id: ID!) {
    reabrirCategoriaTorneio(id: $id) {
      id
      status
    }
  }
`;

export const DELETAR_CATEGORIA_TORNEIO = gql`
  mutation deletarCategoriaTorneio($id: ID!) {
    deletarCategoriaTorneio(id: $id)
  }
`;

export const DELETAR_TODAS_ENTIDADES_CATEGORIA = gql`
  mutation deletarTodasEntidadesCategoria($categoriaTorneioId: ID!) {
    deletarTodasEntidadesCategoria(categoriaTorneioId: $categoriaTorneioId)
  }
`;


export interface CategoriaTorneioPorIdData {
  categoriaTorneioPorId: {
    id: string;
    nome: string;
    aplicarDescontoTorneio: boolean;
    configFilaEspera?: boolean | null;
    inscricoesAtivasCount?: number | null;
    descricao?: string | null;
    genero?: string | null;
    numeroAtletas: number;
    precoInscricao: number;
    quantidadeFases: number;
    regulamento?: string | null;
    status?: string | null;
    vagas?: number | null;
    dataInicio?: string | null;
    dataFim?: string | null;
    dataInicioInscricao?: string | null;
    dataFimInscricao?: string | null;
    dataLimPag?: string | null;
    modeloDisputa?: string | null;
    formatoPartidas?: string | null;
    idadeInicial?: number | null;
    idadeFinal?: number | null;
    idadeCombinada?: number | null;
    quantidadeGrupos?: number | null;
    classificadosPorGrupo?: number | null;
    tamanhoGrupo?: number | null;
    tipoPontuacao?: string | null;
    numeroSets?: number | null;
    gamesPorSets?: number | null;
    tiebreaks?: number | null;
    categoriaLiga?: { id: string; nome?: string | null } | null;
    torneio: {
      id: string;
      nome: string;
      avatarUrl?: string | null;
      dataInscricaoInicio?: string | null;
      dataInscricaoFim?: string | null;
      validarElegibilidade?: boolean | null;
      liga?: { id: string; nome?: string | null } | null;
      // Espelha `CategoriaTorneio.torneio.local` acima: nulável no backend.
      local?: {
        id: string;
        nome: string;
        endereco: {
          id: string;
          logradouro: string;
          numero: string;
          complemento?: string | null;
          bairro: string;
          cidade: {
            id: string;
            nome: string;
            uf: string;
          };
          cep: string;
        };
      } | null;
    };
    fasesCategoria: {
      id: string;
      nome: string;
      numeroAtletas: number;
      dataInicio: string;
      dataFim: string;
      status: string;
      ordem: number;
      tipoGrupoChave?: string | null;
      partidas: PartidaLite[];
    }[];
    partidas?: { id: string }[];
  };
}

export const buscarCategoriaTorneioPorId = async (
  id: string,
): Promise<CategoriaTorneio> => {
  try {
    const { data } = await apolloClient.query<CategoriaTorneioPorIdData>({
      query: CATEGORIA_TORNEIO_POR_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    });
    const c = data.categoriaTorneioPorId;
    return {
      ...c,
      fasesCategoria: c.fasesCategoria.map((f) => ({
        ...f,
        dataInicio: parseISODate(f.dataInicio) ?? new Date(),
        dataFim: parseISODate(f.dataFim) ?? new Date(),
        partidas: f.partidas || [],
      })),
      partidas: c.partidas || [],
    };
  } catch (e) {
    throw new Error('Erro ao buscar categoria de torneio');
  }
};

export type CategoriaTorneioInput = {
  nome: string;
  torneioId: string;
  // Vínculo opcional com a CategoriaLiga escolhida como template (AC-2 do PRD
  // categoria-liga). Aditivo — omitido, o backend não vincula (AC-5).
  categoriaLigaId?: string;
  descricao?: string;
  genero?: GeneroCategoria | string;
  numeroAtletas?: number;
  precoInscricao?: number;
  regulamento?: string;
  aplicarDescontoTorneio?: boolean;
  configFilaEspera?: boolean | null;
  status?: StatusCategoriaTorneio | string;
  vagas?: number;
  dataInicio?: string;
  dataFim?: string;
  dataInicioInscricao?: string;
  dataFimInscricao?: string;
  dataLimPag?: string;
  modeloDisputa?: ModeloDisputa | string | null;
  idadeInicial?: number;
  idadeFinal?: number;
  idadeCombinada?: number;
  quantidadeGrupos?: number;
  classificadosPorGrupo?: number;
  tipoPontuacao?: TipoPontuacao | string | null;
  numeroSets?: number;
  gamesPorSets?: number;
  tiebreaks?: number;
};

export const criarCategoriaTorneio = async (
  input: CategoriaTorneioInput,
): Promise<CategoriaTorneio> => {
  try {
    const { data } = await apolloClient.mutate<{ criarCategoriaTorneio: { id: string } }>({
      mutation: CRIAR_CATEGORIA_TORNEIO,
      variables: { input },
    });
    const createdId = data?.criarCategoriaTorneio?.id;
    if (!createdId) {
      throw new Error('Resposta invalida');
    }
    return await buscarCategoriaTorneioPorId(createdId);
  } catch (e) {
    throw new Error('Erro ao criar categoria de torneio');
  }
};

export const atualizarCategoriaTorneio = async (
  id: string,
  input: CategoriaTorneioInput,
): Promise<CategoriaTorneio> => {
  try {
    const sanitizedInput = Object.fromEntries(
      Object.entries(input).map(([key, value]) => [key, value === undefined ? null : value])
    ) as CategoriaTorneioInput;
    
    const { data } = await apolloClient.mutate<{ atualizarCategoriaTorneio: CategoriaTorneio }>({
      mutation: ATUALIZAR_CATEGORIA_TORNEIO,
      variables: { id, input: sanitizedInput },
    });
    if (!data?.atualizarCategoriaTorneio) {
      throw new Error('Resposta invalida');
    }
    return {
      ...data.atualizarCategoriaTorneio,
      quantidadeFases: 0,
      fasesCategoria: [],
      torneio: { 
        id: '', 
        nome: '',
        local: {
          id: '',
          nome: '',
          endereco: {
            id: '',
            logradouro: '',
            numero: '',
            complemento: null,
            bairro: '',
            cidade: {
              id: '',
              nome: '',
              uf: '',
            },
            cep: '',
          },
        },
      },
    };
  } catch (e) {
    throw new Error('Erro ao atualizar categoria de torneio');
  }
};

export const alterarStatusCategoriaTorneio = async (
  id: string,
  status: StatusCategoriaTorneio,
): Promise<{ id: string; status: string }> => {
  try {
    const { data } = await apolloClient.mutate<{ alterarStatusCategoriaTorneio: { id: string; status: string } }>({
      mutation: ALTERAR_STATUS_CATEGORIA_TORNEIO,
      variables: { id, status },
    });
    if (!data?.alterarStatusCategoriaTorneio) {
      throw new Error('Resposta invalida');
    }
    return data.alterarStatusCategoriaTorneio;
  } catch (e) {
    throw new Error('Erro ao alterar status da categoria de torneio');
  }
};

/**
 * Reabre uma categoria encerrada (ENCERRADA → EM_ANDAMENTO) revertendo também a
 * última fase finalizada, para permitir corrigir resultados. QA 2026-06-23.
 */
export const reabrirCategoriaTorneio = async (
  id: string,
): Promise<{ id: string; status: string }> => {
  try {
    const { data } = await apolloClient.mutate<{ reabrirCategoriaTorneio: { id: string; status: string } }>({
      mutation: REABRIR_CATEGORIA_TORNEIO,
      variables: { id },
    });
    if (!data?.reabrirCategoriaTorneio) {
      throw new Error('Resposta invalida');
    }
    return data.reabrirCategoriaTorneio;
  } catch (e) {
    throw new Error('Erro ao reabrir a categoria de torneio');
  }
};






export interface ResultadoOperacaoLote {
  id: string;
  sucesso: boolean;
  motivo?: string | null;
}

export const ABRIR_INSCRICOES_CATEGORIAS_EM_LOTE = gql`
  mutation AbrirInscricoesCategoriasEmLote($ids: [ID!]!) {
    abrirInscricoesCategoriasEmLote(ids: $ids) {
      id
      sucesso
      motivo
    }
  }
`;

export const abrirInscricoesCategoriasEmLote = async (
  ids: string[],
): Promise<ResultadoOperacaoLote[]> => {
  try {
    const { data } = await apolloClient.mutate<{ abrirInscricoesCategoriasEmLote: ResultadoOperacaoLote[] }>({
      mutation: ABRIR_INSCRICOES_CATEGORIAS_EM_LOTE,
      variables: { ids },
    });
    if (!data?.abrirInscricoesCategoriasEmLote) {
      throw new Error('Resposta invalida');
    }
    return data.abrirInscricoesCategoriasEmLote;
  } catch (e) {
    throw new Error('Erro ao abrir inscrições em lote');
  }
};
